import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Card } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";
import { PROJECT_NEXT } from "@/lib/transitions";

export default function ProjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sources, setSources] = useState([]);
  const [claims, setClaims] = useState([]);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/sources`, { params: { project_id: id } }).then((r) => setSources(r.data)).catch(() => {});
    api.get(`/claims`, { params: { project_id: id } }).then((r) => setClaims(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const transition = async (target) => {
    try {
      await api.post(`/projects/${id}/transition`, { target });
      toast.success(`Moved to ${target}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/notes`, { project_id: id, body: note });
      setNote("");
      toast.success("Note added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) return <div className="p-6 text-zinc-500">Loading…</div>;
  const p = data.project;
  const next = PROJECT_NEXT[p.status] || [];

  return (
    <div className="p-6">
      <Link to="/command-center/projects" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4">
        <ArrowLeft className="h-4 w-4" /> Projects
      </Link>
      <PageHeader title={p.title} subtitle={p.description}>
        <StatusBadge status={p.status} />
      </PageHeader>

      <Card className="p-4 mb-4">
        <div className="label-caps text-zinc-500 mb-2">Advance Workflow</div>
        <div className="flex flex-wrap gap-2">
          {next.length === 0 && <span className="text-sm text-zinc-600">No further transitions.</span>}
          {next.map((t) => (
            <Btn key={t} variant="ghost" data-testid={`project-transition-${t}`} onClick={() => transition(t)}>{t.replace(/_/g, " ")}</Btn>
          ))}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="label-caps text-zinc-500 mb-3">Private Research Notes</div>
          <form onSubmit={addNote} className="flex gap-2 mb-3">
            <input data-testid="note-input" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" />
            <Btn data-testid="note-save-btn" type="submit"><Plus className="h-4 w-4" /></Btn>
          </form>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {data.notes.length === 0 && <p className="text-sm text-zinc-600">No notes.</p>}
            {data.notes.map((n) => (
              <div key={n.id} className="text-sm text-zinc-300 border-l-2 border-zinc-700 pl-3 py-1">
                {n.body}
                <div className="text-[0.65rem] text-zinc-600 font-mono-data mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-zinc-500">Linked Sources ({sources.length})</span>
              <Link to="/command-center/sources" className="text-xs text-emerald-400">Manage</Link>
            </div>
            <ul className="space-y-1.5 text-sm">
              {sources.map((s) => (
                <li key={s.id} className="flex items-center gap-2">
                  {s.is_private && <span className="text-[0.6rem] bg-red-500/15 text-red-300 px-1 rounded-sm">PRIVATE</span>}
                  <span className="text-zinc-300 truncate">{s.title}</span>
                </li>
              ))}
              {sources.length === 0 && <li className="text-zinc-600">None linked.</li>}
            </ul>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps text-zinc-500">Claims ({claims.length})</span>
              <Link to="/command-center/claims" className="text-xs text-emerald-400">Manage</Link>
            </div>
            <ul className="space-y-1.5 text-sm">
              {claims.map((c) => (
                <li key={c.id}>
                  <Link to={`/command-center/claims/${c.id}`} className="text-zinc-300 hover:text-white truncate block">{c.statement}</Link>
                </li>
              ))}
              {claims.length === 0 && <li className="text-zinc-600">None.</li>}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
